/**
 * Vigilante de la sesión del personal de municipio.
 *
 * El backend corta la sesión de una cuenta desactivada / dada de baja (401 USUARIO_INACTIVO) pero solo cuando llega
 * un pedido. Si la persona tiene una pantalla ya cargada y hace clic donde no se manda nada al servidor (el buscador,
 * una fila, un menú), nadie le avisaba hasta el próximo pedido. Este vigilante manda un pedido liviano
 * (GET /api/auth/sesion, que pasa por verificarToken) en tres momentos:
 *   - cada `intervaloMs` (30 s), aunque la persona no toque nada;
 *   - al hacer clic o apretar una tecla, como mucho una vez cada `minEntrePingsMs` (5 s): si el último pedido fue hace
 *     menos que eso, el pedido sale justo cuando se cumple el plazo (nunca se pierde un clic): la persona se entera a
 *     los 5 s como mucho de su primera acción;
 *   - al volver a la pestaña o a la ventana (visibilitychange / focus).
 * No hace nada con la respuesta: si la cuenta está desactivada, el interceptor de axios (configurarAxios.js) recibe el
 * 401, cierra la sesión y el login muestra el cartel. Un error de red o un 503 se ignoran (no cierran nada). Nunca hay
 * dos pedidos a la vez.
 *
 * Devuelve una función para detenerlo (se llama al desmontar la pantalla).
 */
export function iniciarVigilanteSesion({
  ping,
  intervaloMs = 30 * 1000,
  minEntrePingsMs = 5 * 1000,
  hayToken = () => !!sessionStorage.getItem('token'),
  ahora = Date.now,
} = {}) {
  let enCurso = false;
  let ultimoPing = 0;
  let pendiente = null; // pedido diferido por el plazo mínimo entre pedidos

  const hacerPing = async () => {
    if (enCurso || !hayToken()) return;
    enCurso = true;
    ultimoPing = ahora();
    try {
      await ping();
    } catch (e) {
      // Sin red o 503: se ignora. El 401 lo atiende el interceptor de axios.
    } finally {
      enCurso = false;
    }
  };

  const alActuar = () => {
    const espera = minEntrePingsMs - (ahora() - ultimoPing);
    if (espera <= 0) {
      hacerPing();
    } else if (!pendiente) {
      pendiente = setTimeout(() => { pendiente = null; hacerPing(); }, espera);
    }
  };
  const alVolver = () => {
    if (document.visibilityState === 'hidden') return;
    alActuar();
  };

  const temporizador = setInterval(hacerPing, intervaloMs);
  window.addEventListener('click', alActuar, { passive: true, capture: true });
  window.addEventListener('keydown', alActuar, { passive: true, capture: true });
  window.addEventListener('focus', alVolver);
  document.addEventListener('visibilitychange', alVolver);

  return () => {
    clearInterval(temporizador);
    if (pendiente) clearTimeout(pendiente);
    window.removeEventListener('click', alActuar, { capture: true });
    window.removeEventListener('keydown', alActuar, { capture: true });
    window.removeEventListener('focus', alVolver);
    document.removeEventListener('visibilitychange', alVolver);
  };
}
